import React, { useState, useEffect, useRef } from 'react';
import API from '../../services/api';
import { THEME } from '../../constants/theme';

const AddETOCaseModal = ({ isOpen, onClose, onSuccess }) => {
    const [customers, setCustomers] = useState([]);
    const [agents, setAgents] = useState([]);
    const [accounts, setAccounts] = useState([]);

    const [caseForm, setCaseForm] = useState({
        customer_id: '',
        eto_agent_id: '',
        income: '',
        voucher_type: 'CRV',
        account_id: '',
        remarks: ''
    });

    const [custSearch, setCustSearch] = useState('');
    const [showCustDropdown, setShowCustDropdown] = useState(false);
    const [custHighlightIndex, setCustHighlightIndex] = useState(0);

    const [agentSearch, setAgentSearch] = useState('');
    const [showAgentDropdown, setShowAgentDropdown] = useState(false);
    const [agentHighlightIndex, setAgentHighlightIndex] = useState(0);

    const custInputRef = useRef(null);
    const agentInputRef = useRef(null);

    useEffect(() => {
        if (isOpen) {
            fetchCustomers();
            fetchAgents();
            setCaseForm({
                customer_id: '',
                eto_agent_id: '',
                income: '',
                voucher_type: 'CRV',
                account_id: '',
                remarks: ''
            });
            setCustSearch('');
            setAgentSearch('');
        }
    }, [isOpen]);

    useEffect(() => {
        if (caseForm.voucher_type !== 'JV') {
            fetchAccounts(caseForm.voucher_type);
        } else {
            setAccounts([]);
            setCaseForm(prev => ({ ...prev, account_id: '' }));
        }
    }, [caseForm.voucher_type]);

    const fetchCustomers = async () => {
        try {
            const res = await API.get('/v1/customers');
            if (res.data.success) setCustomers(res.data.customers || res.data.data || []);
        } catch (err) {
            console.error("Error fetching customers:", err);
        }
    };

    const fetchAgents = async () => {
        try {
            const res = await API.get('/v1/eto/agents');
            if (res.data.success) setAgents(res.data.data || res.data.agents || []);
        } catch (err) {
            console.error("Error fetching agents:", err);
        }
    };

    const fetchAccounts = async (voucherType) => {
        try {
            const res = await API.get(`/v1/eto/accounts?voucher_type=${voucherType}`);
            if (res.data.success) {
                const accList = res.data.data || res.data.accounts || [];
                setAccounts(accList);
                if (accList.length > 0) {
                    setCaseForm(prev => ({ ...prev, account_id: accList[0].id }));
                }
            }
        } catch (err) {
            console.error("Error fetching accounts:", err);
        }
    };

    const filteredCustomers = customers.filter(c => 
        c.full_name?.toLowerCase().includes(custSearch.toLowerCase()) || 
        c.phone?.includes(custSearch) ||
        c.account_id?.toString().includes(custSearch)
    );

    const filteredAgents = agents.filter(a => 
        a.full_name?.toLowerCase().includes(agentSearch.toLowerCase()) || 
        a.phone?.includes(agentSearch)
    );

    const handleCustKeyDown = (e) => {
        if (!showCustDropdown) setShowCustDropdown(true);
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setCustHighlightIndex(prev => (prev < filteredCustomers.length - 1 ? prev + 1 : 0));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setCustHighlightIndex(prev => (prev > 0 ? prev - 1 : filteredCustomers.length - 1));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (filteredCustomers[custHighlightIndex]) {
                selectCustomer(filteredCustomers[custHighlightIndex]);
            }
        } else if (e.key === 'Escape') {
            setShowCustDropdown(false);
        }
    };

    const selectCustomer = (cust) => {
        setCaseForm(prev => ({ ...prev, customer_id: cust.id }));
        setCustSearch(`${cust.full_name} (Acc ID: ${cust.account_id || 'N/A'})`);
        setShowCustDropdown(false);
    };

    const handleAgentKeyDown = (e) => {
        if (!showAgentDropdown) setShowAgentDropdown(true);
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setAgentHighlightIndex(prev => (prev < filteredAgents.length - 1 ? prev + 1 : 0));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setAgentHighlightIndex(prev => (prev > 0 ? prev - 1 : filteredAgents.length - 1));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (filteredAgents[agentHighlightIndex]) {
                selectAgent(filteredAgents[agentHighlightIndex]);
            }
        } else if (e.key === 'Escape') {
            setShowAgentDropdown(false);
        }
    };

    const selectAgent = (agent) => {
        setCaseForm(prev => ({ ...prev, eto_agent_id: agent.id }));
        setAgentSearch(`${agent.full_name}`);
        setShowAgentDropdown(false);
    };

    const selectedCustomerObj = customers.find(c => c.id === parseInt(caseForm.customer_id));
    const dynamicDescription = selectedCustomerObj 
        ? `eto income recieved from customer ${selectedCustomerObj.full_name}`
        : 'eto income recieved from customer [Select Customer]';

    const handleCaseSubmit = async (e) => {
        e.preventDefault();
        if (!caseForm.customer_id || !caseForm.income) {
            alert('Please fill required fields.');
            return;
        }

        if (caseForm.voucher_type !== 'JV' && !caseForm.account_id) {
            alert('Please select Cash/Bank Account for CRV/BRV.');
            return;
        }

        try {
            const res = await API.post('/v1/eto/cases', caseForm);
            if (res.data.success) {
                alert(`Case Created! Case ID: ${res.data.case_id}`);
                if (onSuccess) onSuccess();
                onClose();
            }
        } catch (err) {
            alert(err.response?.data?.error || 'Error saving case');
        }
    };

    if (!isOpen) return null;

    return (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(4px)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
            <div style={{ backgroundColor: '#fff', padding: '30px', borderRadius: '16px', width: '500px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <h3 style={{ margin: 0, color: '#1e293b' }}>Add ETO Case</h3>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: '#64748b' }}>✕</button>
                </div>
                <form onSubmit={handleCaseSubmit}>

                    {/* CUSTOMER SEARCH */}
                    <div style={{ marginBottom: '15px', position: 'relative' }}>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>Customer *</label>
                        <input
                            ref={custInputRef}
                            type="text"
                            placeholder="Type to search customer or Account ID..."
                            style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', outline: 'none' }}
                            value={custSearch}
                            onChange={(e) => {
                                setCustSearch(e.target.value);
                                setShowCustDropdown(true);
                                setCustHighlightIndex(0);
                            }}
                            onFocus={() => setShowCustDropdown(true)}
                            onKeyDown={handleCustKeyDown}
                            required
                        />

                        {showCustDropdown && filteredCustomers.length > 0 && (
                            <ul style={{
                                position: 'absolute', top: '100%', left: 0, right: 0,
                                backgroundColor: '#fff', border: '1px solid #cbd5e1', borderRadius: '8px',
                                maxHeight: '180px', overflowY: 'auto', zIndex: 200, margin: '4px 0 0 0', padding: 0, listStyle: 'none',
                                boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)'
                            }}>
                                {filteredCustomers.map((c, index) => (
                                    <li
                                        key={c.id}
                                        onClick={() => selectCustomer(c)}
                                        style={{
                                            padding: '10px 14px',
                                            cursor: 'pointer',
                                            fontSize: '13px',
                                            backgroundColor: index === custHighlightIndex ? '#f1f5f9' : '#fff',
                                            color: index === custHighlightIndex ? '#2563eb' : '#334155',
                                            fontWeight: index === custHighlightIndex ? 'bold' : 'normal',
                                            borderBottom: '1px solid #f8fafc'
                                        }}
                                    >
                                        {c.full_name} — <span style={{ color: '#059669', fontWeight: 'bold' }}>Acc ID: #{c.account_id || 'N/A'}</span> ({c.phone || 'No Phone'})
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    {/* AGENT SEARCH */}
                    <div style={{ marginBottom: '15px', position: 'relative' }}>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>ETO Agent (Optional)</label>
                        <input
                            ref={agentInputRef}
                            type="text"
                            placeholder="Type to search agent..."
                            style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', outline: 'none' }}
                            value={agentSearch}
                            onChange={(e) => {
                                setAgentSearch(e.target.value);
                                setShowAgentDropdown(true);
                                setAgentHighlightIndex(0);
                            }}
                            onFocus={() => setShowAgentDropdown(true)}
                            onKeyDown={handleAgentKeyDown}
                        />

                        {showAgentDropdown && filteredAgents.length > 0 && (
                            <ul style={{
                                position: 'absolute', top: '100%', left: 0, right: 0,
                                backgroundColor: '#fff', border: '1px solid #cbd5e1', borderRadius: '8px',
                                maxHeight: '180px', overflowY: 'auto', zIndex: 200, margin: '4px 0 0 0', padding: 0, listStyle: 'none',
                                boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)'
                            }}>
                                {filteredAgents.map((a, index) => (
                                    <li
                                        key={a.id}
                                        onClick={() => selectAgent(a)}
                                        style={{
                                            padding: '10px 14px',
                                            cursor: 'pointer',
                                            fontSize: '13px',
                                            backgroundColor: index === agentHighlightIndex ? '#f1f5f9' : '#fff',
                                            color: index === agentHighlightIndex ? '#2563eb' : '#334155',
                                            fontWeight: index === agentHighlightIndex ? 'bold' : 'normal',
                                            borderBottom: '1px solid #f8fafc'
                                        }}
                                    >
                                        {a.full_name} ({a.phone || 'N/A'})
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>Income Amount *</label>
                        <input
                            type="number"
                            style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', outline: 'none' }}
                            placeholder="Enter income amount"
                            value={caseForm.income}
                            onChange={(e) => setCaseForm({ ...caseForm, income: e.target.value })}
                            required
                        />
                    </div>

                    {/* VOUCHER TYPE */}
                    <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>Voucher Type *</label>
                        <select
                            style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', outline: 'none' }}
                            value={caseForm.voucher_type}
                            onChange={(e) => setCaseForm({ ...caseForm, voucher_type: e.target.value })}
                        >
                            <option value="CRV">CRV (Cash Receipt Voucher)</option>
                            <option value="BRV">BRV (Bank Receipt Voucher)</option>
                            <option value="JV">JV (Journal Voucher)</option>
                        </select>
                    </div>

                    {/* ACCOUNT DROPDOWN */}
                    {caseForm.voucher_type !== 'JV' && (
                        <div style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>
                                Account ({caseForm.voucher_type === 'CRV' ? 'Cash Accounts' : 'Bank Accounts'}) *
                            </label>
                            <select
                                style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', outline: 'none' }}
                                value={caseForm.account_id}
                                onChange={(e) => setCaseForm({ ...caseForm, account_id: e.target.value })}
                                required
                            >
                                {accounts.map(acc => (
                                    <option key={acc.id} value={acc.id}>{acc.title}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>Description (Auto Generated)</label>
                        <input
                            type="text"
                            readOnly
                            style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0', backgroundColor: '#f8fafc', color: '#64748b', fontSize: '14px' }}
                            value={dynamicDescription}
                        />
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>Remarks</label>
                        <textarea
                            style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', outline: 'none' }}
                            placeholder="Optional remarks..."
                            value={caseForm.remarks}
                            onChange={(e) => setCaseForm({ ...caseForm, remarks: e.target.value })}
                        />
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                        <button type="button" onClick={onClose} style={{ padding: '10px 16px', border: '1px solid #cbd5e1', borderRadius: '8px', background: 'none', cursor: 'pointer', fontWeight: '600', color: '#64748b' }}>Cancel</button>
                        <button type="submit" style={{ padding: '10px 20px', backgroundColor: THEME.secondary || '#2563eb', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}>Save Case</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddETOCaseModal;